import { CommonModule } from '@angular/common';
import { Component, Injector } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ArticleReferenceModel } from '@app/models/article-reference.model';
import { IncidentDetailsPageStore } from '../../incident-details-page.store';
import { MatDialog } from '@angular/material/dialog';
import { AddArticleReferenceModalComponent } from './add-article-reference-modal/add-article-reference-modal.component';
import { firstValueFrom } from 'rxjs';
import { ConfirmationModalComponent } from '@app/components/confirmation-modal/confirmation-modal.component';
import { ArticleReferenceCreate } from '../../models/article-reference-create.model';
import { FieldReferenceService } from '../../field-references.service';

@Component({
  standalone: true,
  selector: 'app-article-references',
  imports: [CommonModule, MatIconModule],
  templateUrl: './article-references.component.html',
  styleUrl: './article-references.component.scss',
})
export class ArticleReferencesComponent {
  constructor(
    protected readonly store: IncidentDetailsPageStore,
    protected readonly fieldReferenceService: FieldReferenceService,
    private readonly dialog: MatDialog,
    private readonly injector: Injector,
  ) {}

  protected async addArticle(): Promise<void> {
    const dialogRef = this.dialog.open<
      AddArticleReferenceModalComponent,
      ArticleReferenceCreate | null,
      ArticleReferenceCreate | undefined
    >(AddArticleReferenceModalComponent, {
      minWidth: '400px',
      width: '90vw',
      maxWidth: '564px',
      disableClose: true,
      injector: this.injector,
    });

    const articleReferenceToCreate = await firstValueFrom(dialogRef.afterClosed());

    if (articleReferenceToCreate) {
      const currentState = this.store.articleReferencesToAdd();

      if (currentState) {
        this.store.articleReferencesToAdd.set([
          ...currentState,
          { url: articleReferenceToCreate.url, title: articleReferenceToCreate.name } as ArticleReferenceModel,
        ]);
      }
    }
  }

  protected async deleteReference(articleReferenceToDelete: ArticleReferenceModel): Promise<void> {
    const dialogRef = this.dialog.open<
      ConfirmationModalComponent,
      { title: string; content: string; confirmButtonText: string },
      boolean
    >(ConfirmationModalComponent, {
      data: {
        title: 'Delete reference?',
        content: `This reference will be permanently removed from the incident. This action can't be undone.`,
        confirmButtonText: 'Delete article reference',
      },
    });

    const dialogResult = await firstValueFrom(dialogRef.afterClosed());
    if (dialogResult == false) {
      return;
    }

    const addedInEditMode = this.store.articleReferencesToAdd() ?? [];

    if (addedInEditMode.find((x) => x.url === articleReferenceToDelete.url)) {
      // Check if it's been added in edit mode. If so, remove it from there.
      // We check the URL since there is no id for these references at this point.
      this.store.articleReferencesToAdd.update((referencesToAdd) =>
        referencesToAdd!.filter((reference) => reference.url !== articleReferenceToDelete.url),
      );
    } else {
      // If not, it's an already stored reference and we need to mark it for deletion and remove it from the snapshot collection.
      this.store.articleReferencesSnapshot.update((references) =>
        references!.filter((reference) => reference.id !== articleReferenceToDelete.id),
      );
      this.store.articleReferenceIdsToDelete.update((referencesForDeletion) => [
        ...(referencesForDeletion ?? []),
        articleReferenceToDelete.id,
      ]);
    }
  }
}
